import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import { useJoinGame } from "../../hooks/Game/useJoinGame";
import PlayerIconSelector from "../PlayerIconSelector/PlayerIconSelector";
import ModalCard from "../ui/ModalCard";
import Icon from "../ui/Icon";

type JoinModalProps = {
  code?: string;
  show: boolean;
  onJoin: (code: string) => void;
  onClose: () => void;
};

const JoinModal: React.FC<JoinModalProps> = ({
  code,
  show,
  onJoin,
  onClose,
}) => {
  const [visible, setVisible] = useState(false);
  const [gameCode, setGameCode] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [avatar, setAvatar] = useState({ color: "", animal: "" });

  const { loading, error, joinGame } = useJoinGame();

  useEffect(() => {
    setVisible(show);
  }, [show]);

  useEffect(() => {
    setGameCode(code || "");
  }, [code]);

  const valid = useMemo(
    () => !!gameCode && name?.length > 3 && (!password || password.length > 3),
    [gameCode, name?.length, password],
  );

  useEffect(() => {
    if (name?.length > 3) {
      localStorage.setItem("name", name);
    }
  }, [name]);

  useEffect(() => {
    setName(localStorage.getItem("name") || "");
  }, []);

  const handleSubmit = async () => {
    if (!valid) return;

    const game = await joinGame(
      gameCode,
      {
        name,
        avatar: avatar.animal,
        color: avatar.color,
      },
      password,
    );
    if (game) {
      onJoin(gameCode);
    }
  };

  return (
    <ModalCard
      open={visible}
      onClose={onClose}
      title="Bora pra mesa?"
      icon="enter"
      tone="teal"
      actions={
        <>
          <Button onClick={onClose} variant="text">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <Icon name="play" />
              )
            }
            disabled={!valid}
          >
            Entrar
          </Button>
        </>
      }
    >
      <PlayerIconSelector onChange={setAvatar} />

      <TextField
        label="Seu nome"
        variant="outlined"
        value={name}
        fullWidth
        error={!!name.length && name.length < 4}
        helperText={name.length < 4 ? "Pelo menos 4 letras" : undefined}
        onChange={(event) => setName(event.target.value)}
        required
      />

      <TextField
        label="Código da sala"
        variant="outlined"
        value={gameCode}
        onChange={(event) => setGameCode(event.target.value.toUpperCase())}
        fullWidth
        disabled={!!code}
        required
        slotProps={{
          htmlInput: {
            style: {
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
            },
          },
        }}
      />

      <TextField
        label="Senha da sala (opcional)"
        variant="outlined"
        value={password}
        fullWidth
        onChange={(event) => setPassword(event.target.value)}
      />

      {!!error?.message && <Alert severity="error">{error.message}</Alert>}
    </ModalCard>
  );
};

export default JoinModal;
